import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useMy } from '@/hooks/useMy';
import { DEFAULT_QUERIES } from '@/sections/insights/queryBuilder/savedQueries/defaultQueries';
import { QueryFolders } from '@/sections/insights/queryBuilder/savedQueries/folders';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

/**
 * Hook to fetch and merge default + custom saved queries.
 *
 * Fetches custom queries from backend `/my?label=setting&key=#saved_query`
 * and merges them with DEFAULT_QUERIES. All custom queries are placed in
 * the CUSTOM_QUERIES folder and sorted alphabetically within each folder.
 *
 * @returns {object} - { queries: SavedQuery[], isLoading: boolean }
 *
 * Loading behavior:
 * - Starts with DEFAULT_QUERIES immediately (no loading state for defaults)
 * - Shows isLoading=true while fetching custom queries
 * - On success: merges custom queries into CUSTOM_QUERIES folder
 * - On error: shows toast error and keeps DEFAULT_QUERIES only
 *
 * Error handling:
 * - Network failures: graceful degradation to defaults
 * - Malformed queries: skipped with console.warn (no validation)
 * - Empty response: displays defaults only (no error)
 */
export function useSavedQueries(): {
  queries: SavedQuery[];
  isLoading: boolean;
} {
  const [queries, setQueries] = useState<SavedQuery[]>(DEFAULT_QUERIES);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch custom queries from backend settings API
  // Query key format: #saved_query
  const {
    data: settings,
    status,
    error,
  } = useMy(
    {
      resource: 'setting',
      query: '#saved_query', // Fetch all saved query settings
    },
    {
      staleTime: 30000, // 30s cache (appropriate for settings data)
      gcTime: 300000, // 5min cache retention
    }
  );

  useEffect(() => {
    if (status === 'pending') {
      setIsLoading(true);
      return;
    }

    if (status === 'error') {
      console.error('Failed to load custom queries:', error);
      toast.error('Failed to load custom queries');
      setQueries(DEFAULT_QUERIES); // Fallback to defaults
      setIsLoading(false);
      return;
    }

    if (status === 'success') {
      try {
        // Extract custom queries from settings
        const customQueries: SavedQuery[] = (settings || [])
          .map(setting => {
            try {
              // Backend returns setting.value as SavedQuery
              const query = setting.value as SavedQuery;

              // Ensure query has required fields
              if (!query.id || !query.name || !query.query) {
                console.warn(
                  'Skipping malformed saved query:',
                  setting.name,
                  query
                );
                return null;
              }

              // Force all custom queries into CUSTOM_QUERIES folder
              const customQuery: SavedQuery = {
                ...query,
                folderId: QueryFolders.CUSTOM_QUERIES,
              };

              return customQuery;
            } catch (err) {
              console.warn('Error parsing saved query:', setting.name, err);
              return null;
            }
          })
          .filter((q): q is SavedQuery => q !== null); // Remove malformed entries

        // Merge default + custom queries
        const merged = [...DEFAULT_QUERIES, ...customQueries];

        // Sort alphabetically within each folder
        const sorted = sortQueriesByFolder(merged);

        setQueries(sorted);
      } catch (err) {
        console.error('Error processing custom queries:', err);
        toast.error('Failed to process custom queries');
        setQueries(DEFAULT_QUERIES); // Fallback to defaults
      } finally {
        setIsLoading(false);
      }
    }
  }, [settings, status, error]);

  return { queries, isLoading };
}

/**
 * Sort queries by folder, preserving order for default queries while sorting custom queries.
 *
 * Strategy:
 * - DEFAULT queries (from SECURITY_ADVISORIES, , etc.): Preserve original array order
 * - CUSTOM queries: Sort alphabetically by name
 *
 * This ensures CVE ordering in Security Advisories remains Year DESC, Number DESC
 * while custom user queries are alphabetically sorted for easy lookup.
 */
function sortQueriesByFolder(queries: SavedQuery[]): SavedQuery[] {
  // Group queries by folder
  const queryMap = new Map<string, SavedQuery[]>();

  queries.forEach(query => {
    if (!queryMap.has(query.folderId)) {
      queryMap.set(query.folderId, []);
    }
    queryMap.get(query.folderId)!.push(query);
  });

  // // Sort within folders
  // queryMap.forEach((folderQueries, folderId) => {
  //   // Only sort CUSTOM_QUERIES alphabetically
  //   // All other folders (system folders) preserve original order
  //   if (folderId === QueryFolders.CUSTOM_QUERIES) {
  //     folderQueries.sort((a, b) => a.name.localeCompare(b.name));
  //   }
  //   // For system folders (SECURITY_ADVISORIES, CONCERNING_EXPOSURE_QUERIES, etc.),
  //   // keep the original array order (no sorting)
  // });

  // Reconstruct array with sorted folders
  const result: SavedQuery[] = [];
  queryMap.forEach((folderQueries, _folderId) => {
    result.push(...folderQueries);
  });

  // Sort by folder ID to maintain consistent folder order
  return result.sort((a, b) => {
    if (a.folderId !== b.folderId) {
      return a.folderId.localeCompare(b.folderId);
    }

    return 0;
  });
}
